import { useId, useState } from 'react';
import { Alert, AlertDescription, AlertTitle, Input, Label } from '@design-system';
import { isValidEmailFormat } from '@shared';
import { isWithin, NumberField } from '../components/number-field';
import { StepFrame } from '../components/step-frame';
import { useConsumerClient } from '../hooks/use-consumer-client';
import { useConsumerFlow } from '../hooks/use-consumer-flow';
import { useConsumerPlan } from '../hooks/use-consumer-plan';
import { usePremiumRangeRequest } from '../hooks/use-consumer-requests';
import { PAYMENT_SCHEDULE_LABELS } from '../lib/format';
import type { ConsumerStepProps } from './step-props';

const MAX_PREMIUM = 1_000_000;

export function PremiumRangeStep({ clientId }: ConsumerStepProps) {
  const { finish } = useConsumerFlow(clientId);
  const { plan } = useConsumerPlan(clientId);
  const { client } = useConsumerClient(clientId);
  const request = usePremiumRangeRequest(clientId);
  const schedule = plan?.paymentSchedule ?? 'tenPay';
  const unit = schedule === 'singlePay' ? 'single premium' : schedule === 'tenPay' ? 'per year' : 'per month';
  const [minPremium, setMinPremium] = useState<number | null>(null);
  const [maxPremium, setMaxPremium] = useState<number | null>(null);
  const [email, setEmail] = useState(client?.clientEmail ?? '');
  const [error, setError] = useState<string | null>(null);
  const emailId = useId();

  const send = () => {
    if (!isWithin(minPremium, 0, MAX_PREMIUM) || !isWithin(maxPremium, 1, MAX_PREMIUM) || maxPremium < minPremium) {
      setError('Enter a minimum and a maximum, with the maximum at least the minimum.');
      return;
    }
    if (!isValidEmailFormat(email)) {
      setError('Enter an email address we can reply to.');
      return;
    }
    setError(null);
    request.mutate({ paymentSchedule: schedule, minPremium, maxPremium, email });
  };

  if (request.isSuccess) {
    return (
      <StepFrame
        step="premium-range"
        title="Your request is on its way."
        subtitle="Our team will email options in your range. Your current choice stays in place while you explore."
        primary={{ label: 'Keep going', onClick: () => finish('options') }}
      >
        <Alert variant="success">
          <AlertTitle>Request sent</AlertTitle>
          <AlertDescription>We’ll reply to {email}.</AlertDescription>
        </Alert>
      </StepFrame>
    );
  }

  return (
    <StepFrame
      step="premium-range"
      title="What premium range would you prefer?"
      subtitle={`${PAYMENT_SCHEDULE_LABELS[schedule]} · our team will email options in this range.`}
      onBack={() => finish('options')}
      help={<p>This sends a request for a different budget. It doesn’t reprice the options on this screen or promise more coverage.</p>}
      primary={{ label: 'Send my request', onClick: send, loading: request.isPending }}
      secondary={{ label: 'Cancel', onClick: () => finish('options') }}
      error={error ?? (request.isError ? 'We couldn’t send your request. Please try again.' : null)}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <NumberField label={`Minimum ${unit} ($)`} value={minPremium} onChange={setMinPremium} min={0} step={50} />
        <NumberField label={`Maximum ${unit} ($)`} value={maxPremium} onChange={setMaxPremium} min={1} step={50} />
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor={emailId}>Email</Label>
        <Input
          id={emailId}
          type="email"
          autoComplete="email"
          className="bg-white"
          value={email}
          onChange={event => setEmail(event.target.value)}
        />
      </div>
    </StepFrame>
  );
}
